// Feature: features/nma-dx-coding-guidelines.md
import React from 'react';
import {
  Box,
  Heading,
  Text,
  Alert,
  VStack,
  HStack,
  Badge,
  List,
  ListItem,
  Code,
  useColorModeValue,
} from '@chakra-ui/react';

const PRIORITY_ROWS = [
  {
    score: '100',
    colorScheme: 'green',
    codes: '95941 / G0453 (Pro), 95940 (Tech)',
    description: 'Continuous Intraoperative Neurophysiology Monitoring Time.',
  },
  {
    score: '98',
    colorScheme: 'green',
    codes: '95939',
    description: 'Central Motor Evoked Potential (MEP) study — Upper & Lower Limbs.',
  },
  {
    score: '97',
    colorScheme: 'teal',
    codes: '95822',
    description: 'Electroencephalogram (EEG) — Recording in coma or sleep.',
  },
  {
    score: '96',
    colorScheme: 'green',
    codes: '95938',
    description: 'Somatosensory Evoked Potential (SSEP) study — Upper & Lower Limbs.',
  },
  {
    score: '95',
    colorScheme: 'green',
    codes: '95929',
    description: 'Central Motor Evoked Potential (MEP) study — Lower Limbs.',
  },
  {
    score: '94',
    colorScheme: 'teal',
    codes: '95829',
    description: 'Electrocorticogram (ECoG) at surgery.',
  },
  {
    score: '93',
    colorScheme: 'green',
    codes: '95926',
    description: 'Somatosensory Evoked Potential (SSEP) study — Lower Limbs.',
  },
  {
    score: '92',
    colorScheme: 'teal',
    codes: '95930',
    description: 'Visual Evoked Potential (VEP) testing.',
  },
  {
    score: '91',
    colorScheme: 'teal',
    codes: '95955',
    description: 'Electroencephalogram (EEG) during non-intracranial surgery.',
  },
  {
    score: '90 – 84',
    colorScheme: 'purple',
    codes: '95907 – 95913',
    description: 'Nerve Conduction Studies (95907 = 90 down to 95913 = 84).',
  },
  {
    score: '83',
    colorScheme: 'blue',
    codes: '51785',
    description: 'Needle EMG of anal or urethral sphincter.',
  },
  {
    score: '82 / 81 / 80',
    colorScheme: 'blue',
    codes: '95886 / 95887 / 95885',
    description: 'Needle EMG with Nerve Conduction (Complete, Non-Extremity, Limited).',
  },
  {
    score: '79',
    colorScheme: 'blue',
    codes: '95861',
    description: 'Needle EMG — 2 extremities (5+ muscles each).',
  },
  {
    score: '78',
    colorScheme: 'blue',
    codes: '95865',
    description: 'Needle EMG of larynx (RLN / Vocalis).',
  },
  {
    score: '77',
    colorScheme: 'blue',
    codes: '95868',
    description: 'Needle EMG — Cranial nerve innervated muscles, bilateral.',
  },
  {
    score: '76',
    colorScheme: 'blue',
    codes: '95870',
    description: 'Needle EMG — Limited study of muscles in 1 extremity or axial muscles.',
  },
  {
    score: '75',
    colorScheme: 'orange',
    codes: '92653',
    description: 'Brainstem Auditory Evoked Response (BAER).',
  },
  {
    score: '74',
    colorScheme: 'orange',
    codes: '95999 / 95937',
    description: 'Unlisted neurological procedure / Train of Four (TOF) NMJ testing.',
  },
];

const NMACptCodingLogic = () => {
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box maxW="900px">
      <Heading as="h1" size="xl" mb={6} color="brand.700">
        NMA CPT Code Logic
      </Heading>

      <VStack spacing={10} align="stretch">
        {/* Section 1 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            CPT Code Order on the Revedy Coding Report
          </Heading>

          <VStack spacing={4} align="stretch">
            <Text>
              Within both the <strong>Professional Coding Results</strong> and{' '}
              <strong>Technical Coding Results</strong> sections of the Revedy Coding Report (as well as the outbound JSON payload), CPT codes are ordered from{' '}
              <strong>highest to lowest</strong> using the <strong>IONM Priority Ranking Scores provided by NMA</strong>:
            </Text>

            <Alert
              status="info"
              variant="left-accent"
              flexDirection="column"
              alignItems="flex-start"
              bg={sectionBg}
              borderColor="brand.500"
              p={5}
              borderRadius="md"
            >
              <Text fontWeight="bold" mb={3}>
                IONM Priority Ranking Scale provided by NMA (High to Low):
              </Text>

              <VStack spacing={3} align="stretch" w="100%">
                {PRIORITY_ROWS.map((row) => (
                  <HStack key={row.score} align="flex-start" spacing={3}>
                    <Badge
                      colorScheme={row.colorScheme}
                      fontSize="sm"
                      px={2}
                      py={0.5}
                      minW="95px"
                      textAlign="center"
                      flexShrink={0}
                    >
                      Rank {row.score}
                    </Badge>
                    <Text fontSize="sm">
                      <Code fontWeight="bold" mr={2}>
                        {row.codes}
                      </Code>
                      {row.description}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Alert>

            <Box p={4} bg={sectionBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <Text fontWeight="bold" mb={2}>
                Additional Ordering Rules:
              </Text>
              <List spacing={2} pl={4} styleType="disc">
                <ListItem>
                  <strong>Repeat Procedures (-76 Modifier) &amp; Multiple Lines:</strong> When an SSEP or MEP code is split into two lines for a repeat procedure, the base line (<Code>26</Code> or <Code>TC</Code>) is listed first, immediately followed by the repeat line (<Code>-76</Code>). Multiple lines sharing the same CPT code (such as multiple <Code>95870</Code> lines) remain grouped together in their generated sequence.
                </ListItem>
                <ListItem>
                  <strong>Payer &amp; Carrier Rule Conversions:</strong> Priority scores are assigned before payer/carrier rules execute. When a deterministic rule converts a code (for example, <Code>95941 -&gt; G0453</Code> or <Code>95999 -&gt; 95937</Code>), the converted code retains the priority rank of the original code (<Code>100</Code> for <Code>G0453</Code> and <Code>74</Code> for <Code>95937</Code>).
                </ListItem>
              </List>
            </Box>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default NMACptCodingLogic;
